import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Flex, Text, Button, Center } from '@televet/kibble-ui';
import { PetsListItem } from './PetsListItem';
import LoadingIndicator from 'shared/components/LoadingIndicator';
import { useSidePanelSearch } from '../../../state/providers/SearchPanelProvider';
import { Mode } from '../../../shared/types/enum/Mode';
import { useGetParentPets } from '../../../shared/hooks/useGetParentPets';
import { SidePanelPetParentFragment } from 'shared/types/graphql';

interface PetsListProps {
  petParent: SidePanelPetParentFragment;
}

export const PetsList = ({ petParent }: PetsListProps): JSX.Element => {
  const { searchPanelMode, viewAddClientOrPatient } = useSidePanelSearch();
  const {
    pets: currentPets,
    petCount: totalPetCount,
    fetchMore: fetchMorePets,
    loading: loadingPets,
    refetchPets,
  } = useGetParentPets(petParent.id);

  // Determine if the parent has inactive pets
  // this will condition the visibility for "Show more | show Inactive" buttons
  const { petCount: inactivePetCount = 0 } = useGetParentPets(petParent.id, false);
  const [isShowInactiveButtonVisible, setIsShowInactiveButtonVisible] = useState(true);

  const filteredPets = useMemo(() => {
    return !!isShowInactiveButtonVisible
      ? currentPets.filter((pet) => pet.computedIsActive && !pet.computedIsDeceased)
      : currentPets;
  }, [isShowInactiveButtonVisible, currentPets]);

  const hasInactivePets = useMemo(() => {
    return currentPets.filter((pet) => !pet.computedIsActive || pet.computedIsDeceased).length;
  }, [currentPets]);

  // Determine when to display the 'Show More' button:
  // a) If the 'Show Inactive' button was pressed and not all available pets have been fetched.
  // b) If the sum of active pets and inactive count is less than the total number of pets fetched.
  const displayShowMoreButton =
    isShowInactiveButtonVisible === false
      ? currentPets.length !== totalPetCount
      : currentPets.length + inactivePetCount < totalPetCount;

  // Determine when to display the 'Show Inactive' button:
  // Display the button if it hasn't been pressed and either:
  // a) There are inactive pets among the fetched ones.
  // b) The sum of active pets and inactive count is greater than or equal to the total number of pets.
  const displayShowInactiveButton =
    isShowInactiveButtonVisible &&
    (hasInactivePets || (inactivePetCount > 0 && currentPets.length + inactivePetCount >= totalPetCount));

  const handleShowInactivePets = useCallback(() => {
    setIsShowInactiveButtonVisible(false);
    // Fetch the first 10 inactive pets if all active pets has been displayed pets and match a 10th digit,
    // Example: If there are 50 pets in total, 40 are displayed, and the remaining 10 are inactive, fetch them to prevent a weird interaction.
    if (currentPets.length + inactivePetCount == totalPetCount) {
      fetchMorePets();
    }
  }, [currentPets.length, fetchMorePets, inactivePetCount, totalPetCount]);

  useEffect(() => {
    setIsShowInactiveButtonVisible(true);
    // Setting petParent here ensures that the value is reset when navigating between profiles
  }, [petParent]);

  return (
    <Box className="PetParentProfile__PetsList">
      <Flex align="center" justify="space-between" px={6} mb={2}>
        <Text fontWeight="bold">Patients</Text>
        {searchPanelMode !== Mode.LinkToChannel && (
          <Button
            onClick={(): void => {
              viewAddClientOrPatient({ selectedClinicPetParent: petParent });
            }}
            size="sm"
            variant="ghost"
            iconName="plus"
            data-mixpanel-name="Add new patient from client profile button"
          >
            Add New
          </Button>
        )}
      </Flex>
      {filteredPets.map((pet) => (
        <PetsListItem pet={pet} petParentId={petParent.id} key={pet.id} refetchPets={refetchPets} />
      ))}

      {displayShowMoreButton && (
        <Center py={2}>
          {loadingPets ? (
            <LoadingIndicator />
          ) : (
            <Button variant="ghost" size="sm" isLoading={loadingPets} onClick={(): Promise<void> => fetchMorePets()}>
              Show More
            </Button>
          )}
        </Center>
      )}
      {!filteredPets.length && (
        <Center px={3}>
          <Text size="sm" variant="subtle">
            {loadingPets ? 'Loading...' : `No patients found`}
          </Text>
        </Center>
      )}
      {displayShowInactiveButton && (
        <Center pt={2}>
          <Button iconName="eyeOpen" size="xs" variant="ghost" onClick={handleShowInactivePets}>
            Show Inactive Patients
          </Button>
        </Center>
      )}
    </Box>
  );
};
